'use client';

import React, { createContext, useContext, ReactNode, useMemo } from 'react';
import { QueryClient, QueryClientProvider, useQuery, UseQueryResult } from '@tanstack/react-query';
export interface PoolVolumeData {
  daily_apr_percentage: number;
  daily_dex_fee_usd: number;
  daily_fee_total_usd: number;
  daily_swap_volume_usd: number;
  daily_user_fee_usd: number;
  num_swap_logs: number;
  pool_address: string;
  price0: number;
  price1: number;
  symbol0: string;
  symbol1: string;
  token0: string;
  token1: string;
  volume_token0_hr: number;
  volume_token0_raw: number;
  volume_token1_hr: number;
  volume_token1_raw: number;
}
export interface PoolVolumeResponse {
  pools: PoolVolumeData[];
  target_date: string;
  from_block: number;
  to_block: number;
  total_daily_dex_fee_usd: number;
  total_daily_fee_total_usd: number;
  total_daily_swap_volume_usd: number;
  total_daily_user_fee_usd: number;
  pools_processed_count?: number;
  date_used?: string;
  message?: string;
}
export interface V2AnalyticsPool {
  pool_address: string;
  price0: number;
  price1: number;
  reserve0_hr: number;
  reserve1_hr: number;
  token0: string;
  token0_symbol: string;
  token1: string;
  token1_symbol: string;
  tvl_usd: number;
  fee?: number;
}
export interface V2AnalyticsResponse {
  pools: V2AnalyticsPool[];
  total_tvl: number;
}
export interface V3AnalyticsPool {
  creation_block: number;
  error: boolean;
  error_message?: string;
  fee: number;
  pool_address: string;
  tickSpacing: number;
  token0: string;
  token0_symbol: string;
  token1: string;
  token1_symbol: string;
  tvl_usd: number;
}
export interface V3AnalyticsResponse {
  cumulative_tvl: number;
  pools: V3AnalyticsPool[];
}
export interface V2PoolData {
  daily_swap_volume_usd: number;
  num_swap_logs: number;
  pool_address: string;
  price0: number;
  price1: number;
  symbol0: string;
  symbol1: string;
  token0: string;
  token1: string;
  volume_token0_hr: number;
  volume_token1_hr: number;
  tvl_usd: number;
  daily_fees_usd: number;
}
export interface V2PoolResponse {
  pools_data: V2PoolData[];
  total_daily_swap_volume_usd: number;
  total_tvl_usd: number;
  date_used: string;
  message: string;
}
export interface EnrichedV2Pool extends V2PoolData {
  // Already enriched from our API
}
export interface EnrichedV3Pool extends PoolVolumeData {
  tvl_usd: number;
  fee: number;
  creation_block: number;
  tickSpacing: number;
}
export const poolDataKeys = {
  all: ['pool-data'] as const,
  v2: () => [...poolDataKeys.all, 'v2'] as const,
  v3: () => [...poolDataKeys.all, 'v3'] as const,
  v2Date: (date?: string) => [...poolDataKeys.v2(), date] as const,
  v3Date: (date?: string) => [...poolDataKeys.v3(), date] as const
};
export const analyticsKeys = {
  all: ['analytics'] as const,
  v2: () => [...analyticsKeys.all, 'v2'] as const,
  v3: () => [...analyticsKeys.all, 'v3'] as const
};
async function fetchV2PoolData(date?: string): Promise<V2PoolResponse> {
  const url = date ? `/api/pools/V2?date=${date}` : '/api/pools/V2';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch V2 pool data: ${response.status}`);
  }
  return response.json();
}
async function fetchV3PoolData(date?: string): Promise<PoolVolumeResponse> {
  const url = date ? `/api/pools/V3?date=${date}` : '/api/pools/V3';
  const response = await fetch(url);
  if (!response.ok) {
    throw new Error(`Failed to fetch V3 pool data: ${response.status}`);
  }
  return response.json();
}
async function fetchV3Analytics(): Promise<V3AnalyticsResponse> {
  const response = await fetch('/api/analytics/V3');
  if (!response.ok) {
    throw new Error(`Failed to fetch V3 analytics data: ${response.status}`);
  }
  return response.json();
}
async function fetchV2Analytics(): Promise<V2AnalyticsResponse> {
  const response = await fetch('/api/analytics/V2');
  if (!response.ok) {
    throw new Error(`Failed to fetch V2 analytics data: ${response.status}`);
  }
  return response.json();
}
function useV2PoolData(date?: string): UseQueryResult<V2PoolResponse, Error> {
  return useQuery({
    queryKey: poolDataKeys.v2Date(date),
    queryFn: () => fetchV2PoolData(date),
    staleTime: date ? Infinity : 6 * 60 * 60 * 1000
  });
}
function useV3PoolData(date?: string): UseQueryResult<PoolVolumeResponse, Error> {
  return useQuery({
    queryKey: poolDataKeys.v3Date(date),
    queryFn: () => fetchV3PoolData(date),
    staleTime: date ? Infinity : 6 * 60 * 60 * 1000
  });
}
function useV3Analytics(): UseQueryResult<V3AnalyticsResponse, Error> {
  return useQuery({
    queryKey: analyticsKeys.v3(),
    queryFn: fetchV3Analytics,
    staleTime: 6 * 60 * 60 * 1000 // 6 hours
  });
}
function useV2Analytics(): UseQueryResult<V2AnalyticsResponse, Error> {
  return useQuery({
    queryKey: analyticsKeys.v2(),
    queryFn: fetchV2Analytics,
    staleTime: 6 * 60 * 60 * 1000 // 6 hours
  });
}
interface PoolsContextData {
  v2PoolData: UseQueryResult<V2PoolResponse, Error>;
  v3PoolData: UseQueryResult<PoolVolumeResponse, Error>;
  v2Analytics: UseQueryResult<V2AnalyticsResponse, Error>;
  v3Analytics: UseQueryResult<V3AnalyticsResponse, Error>;
  enrichedV2Pools: EnrichedV2Pool[];
  enrichedV3Pools: EnrichedV3Pool[];
  isLoading: boolean;
  isError: boolean;
  error: Error | null;
  getPoolApr: (poolAddress: string, version: 'v2' | 'v3') => number | null;
  getEnrichedPool: (poolAddress: string, version: 'v2' | 'v3') => EnrichedV2Pool | EnrichedV3Pool | null;
  refreshPoolData: (version?: 'v2' | 'v3' | 'all') => void;
}
const PoolsContext = createContext<PoolsContextData | undefined>(undefined);
interface NewPoolsProviderProps {
  children: ReactNode;
  date?: string;
}
function NewPoolsProviderInternal({
  children,
  date
}: NewPoolsProviderProps) {
  const v2PoolDataQuery = useV2PoolData(date);
  const v3PoolDataQuery = useV3PoolData(date);
  const v2AnalyticsQuery = useV2Analytics(); // Keep for backward compatibility
  const v3AnalyticsQuery = useV3Analytics();
  const enrichedV2Pools = useMemo(() => {
    if (!v2PoolDataQuery.data?.pools_data) return [];

    // V2 pools are already enriched from our API endpoint
    return v2PoolDataQuery.data.pools_data;
  }, [v2PoolDataQuery.data]);
  const enrichedV3Pools = useMemo(() => {
    if (!v3AnalyticsQuery.data?.pools || !v3PoolDataQuery.data?.pools) return [];
    return v3PoolDataQuery.data.pools.map(volumePool => {
      const analyticsPool = v3AnalyticsQuery.data!.pools.find(pool => pool.pool_address.toLowerCase() === volumePool.pool_address.toLowerCase());
      return {
        ...volumePool,
        symbol0: volumePool.symbol0 || analyticsPool?.token0_symbol || '',
        symbol1: volumePool.symbol1 || analyticsPool?.token1_symbol || '',
        tvl_usd: analyticsPool?.tvl_usd || 0,
        fee: analyticsPool?.fee || 0,
        creation_block: analyticsPool?.creation_block || 0,
        tickSpacing: analyticsPool?.tickSpacing || 0
      };
    });
  }, [v3PoolDataQuery.data, v3AnalyticsQuery.data]);
  const getPoolApr = (poolAddress: string, version: 'v2' | 'v3'): number | null => {
    if (version === 'v2') {
      if (!v2PoolDataQuery.data?.pools_data) return null;
      const pool = v2PoolDataQuery.data.pools_data.find(p => p.pool_address.toLowerCase() === poolAddress.toLowerCase());
      // V2 pools don't have APR in our current data structure
      return null;
    } else {
      if (!v3PoolDataQuery.data?.pools) return null;
      const pool = v3PoolDataQuery.data.pools.find(p => p.pool_address.toLowerCase() === poolAddress.toLowerCase());
      return pool ? pool.daily_apr_percentage : null;
    }
  };
  const getEnrichedPool = (poolAddress: string, version: 'v2' | 'v3'): EnrichedV2Pool | EnrichedV3Pool | null => {
    const pools = version === 'v2' ? enrichedV2Pools : enrichedV3Pools;
    return pools.find(pool => pool.pool_address.toLowerCase() === poolAddress.toLowerCase()) || null;
  };
  const refreshPoolData = (version: 'v2' | 'v3' | 'all' = 'all') => {
    if (version === 'v2' || version === 'all') {
      v2PoolDataQuery.refetch();
      // V2 analytics not needed anymore since data is enriched in API
    }
    if (version === 'v3' || version === 'all') {
      v3PoolDataQuery.refetch();
      v3AnalyticsQuery.refetch();
    }
  };
  const contextValue: PoolsContextData = {
    v2PoolData: v2PoolDataQuery,
    v3PoolData: v3PoolDataQuery,
    v2Analytics: v2AnalyticsQuery,
    v3Analytics: v3AnalyticsQuery,
    enrichedV2Pools,
    enrichedV3Pools,
    isLoading: v2PoolDataQuery.isLoading || v3PoolDataQuery.isLoading || v3AnalyticsQuery.isLoading,
    isError: v2PoolDataQuery.isError || v3PoolDataQuery.isError || v3AnalyticsQuery.isError,
    error: v2PoolDataQuery.error || v3PoolDataQuery.error || v3AnalyticsQuery.error,
    getPoolApr,
    getEnrichedPool,
    refreshPoolData
  };
  return <PoolsContext.Provider value={contextValue} data-sentry-element="unknown" data-sentry-component="NewPoolsProviderInternal" data-sentry-source-file="PoolsContext.tsx">{children}</PoolsContext.Provider>;
}
export function NewPoolsProvider({
  children,
  date
}: NewPoolsProviderProps) {
  const [queryClient] = React.useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 6 * 60 * 60 * 1000,
        gcTime: 24 * 60 * 60 * 1000,
        retry: 2,
        refetchOnWindowFocus: false,
        refetchOnMount: 'always'
      }
    }
  }));
  return <QueryClientProvider client={queryClient} data-sentry-element="QueryClientProvider" data-sentry-component="NewPoolsProvider" data-sentry-source-file="PoolsContext.tsx">
      <NewPoolsProviderInternal date={date} data-sentry-element="NewPoolsProviderInternal" data-sentry-source-file="PoolsContext.tsx">{children}</NewPoolsProviderInternal>
    </QueryClientProvider>;
}
export function usePools() {
  const context = useContext(PoolsContext);
  if (context === undefined) {
    throw new Error('usePools must be used within a NewPoolsProvider');
  }
  return context;
}